<template>
  <div class="se-container">
    <Suspense>
      <EventDetails
        :event="event"
        :is-editable="isEditable"
        :has-changes="hasChanges"
        :props-edited="propsEdited"
        :mode="
          isEditable
            ? isDraftEvent
              ? EventDetailsMode.create
              : EventDetailsMode.edit
            : EventDetailsMode.view
        "
        :task="task"
        :is-draft-event="isDraftEvent"
        @update-planner-view="(date) => $emit('updatePlannerView', date)"
        @confirm-changes="$emit('confirmChanges')"
        @kill-event-details="$emit('killEventDetails')"
        @open-custom-recurrence="$emit('openCustomRecurrence')"
      />
      <template #fallback>
        <Loader>
          <div class="loader">
            <BasePaneHeader :has-nav="false" :is-hideable="false">
              <BaseSkeletonList :size="1" class="header" />
            </BasePaneHeader>
            <BaseSkeletonList class="body" />
          </div>
        </Loader>
      </template>
    </Suspense>
    <div v-if="hasChanges || isDraftEvent" class="actions">
      <BaseButton
        :text="isDraftEvent ? 'Cancel' : 'Discard'"
        kind="main"
        :tooltip="{
          text: isDraftEvent ? 'Cancel' : 'Discard',
          shortcut: formActions.cancel.shortcutsDisplay,
        }"
        tooltip-display="always"
        tint="red"
        @click="$emit('discardChanges')"
      />
      <div class="spacer" />
      <BaseButton
        :text="isDraftEvent ? 'Create' : 'Update'"
        :tooltip="{
          text: isDraftEvent ? 'Create' : 'Update',
          shortcut: formActions.validate.shortcutsDisplay,
        }"
        tooltip-display="always"
        kind="main"
        @click="$emit('confirmChanges')"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, toRefs } from "vue";
import { NavigationGuardNext, onBeforeRouteLeave } from "vue-router";

import type { FullEvent, FullTask } from "@ctrl/controller-rpc";

import { formActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import EventDetails from "@/components/planner/sidebar/EventDetails.vue";
import { RouteNames } from "@/routes/types";

import type { PropsEdited } from "../controllers/event";
import { EventDetailsMode } from "../types";

export default defineComponent({
  components: {
    Loader,
    BaseButton,
    BasePaneHeader,
    BaseSkeletonList,
    EventDetails,
  },
  props: {
    event: { type: Object as PropType<FullEvent>, required: true },
    hasChanges: { type: Boolean, required: true },
    isDraftEvent: { type: Boolean, required: true },
    isEditable: { type: Boolean, required: true },
    isUpdating: { type: Boolean, required: true },
    propsEdited: { type: Object as PropType<PropsEdited>, required: true },
    task: { type: Object as PropType<FullTask>, required: false },
  },
  emits: {
    confirmChanges: () => true,
    discardChanges: () => true,
    killEventDetails: (_nextRoute?: NavigationGuardNext, _force = false) =>
      true,
    openCustomRecurrence: () => true,
    updatePlannerView: (_: Date) => true,
  },
  setup(props, { emit }) {
    const { hasChanges, event, isDraftEvent, isUpdating } = toRefs(props);
    const logger = inject.logger("SidebarEvent");
    onBeforeRouteLeave((to, _from, next) => {
      // Diagnostic trail for events vanishing right after creation.
      logger.info("Route leave with open event details", {
        eventId: event.value.id,
        to: to.path,
        hasChanges: hasChanges.value,
        isDraftEvent: isDraftEvent.value,
        isUpdating: isUpdating.value,
      });
      if (
        hasChanges.value &&
        !isUpdating.value &&
        (!isDraftEvent.value ||
          (to.name !== RouteNames.Event && !to.path.includes(event.value.id)))
      ) {
        emit("killEventDetails", next);
      } else {
        emit("killEventDetails", undefined, true);
        next();
      }
    });
    return {
      EventDetailsMode,
      formActions,
    };
  },
});
</script>
<style lang="scss" scoped>
.se-container {
  display: flex;
  flex: 1;
  max-width: 100%;

  .actions {
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: absolute;
    bottom: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    padding: 30px 20px 20px 20px;
    .spacer {
      flex: 1;
    }
  }
  .loader {
    display: flex;
    flex-direction: column;
    flex: 1;
    .header {
      width: 100%;
    }
    .body {
      padding-top: 16px;
      padding-inline-start: 12px;
    }
  }
}
</style>
